/**
 * ProfileInformationCard — "Information" card on the Settings page.
 *
 * Display Name + Location inputs (Display Name is always visible), Birthday
 * month/day selects, and a "Show on my profile" checkbox group controlling
 * the visibility of Location / Birthday / Member Since. Per-field visibility
 * is set via the checkbox group; no per-field toggle switches. The avatar
 * and the save action live elsewhere (gradient header / bottom save bar).
 *
 * @route /profile/edit
 * @parent CommunityProfileSettingsPage
 */

import React, { useMemo, useCallback } from 'react'
import { H2 } from '../../../../../Heading/index'
import ChevronDown from '../../../../../Icon/icons/ChevronDown/index'
import EyeShowSolid from '../../../../../Icon/icons/EyeShowSolid/index'
import LocationAutocomplete from '../../../../../LocationAutocomplete/index'
import { useTranslatedText } from '../../../../../StaticText/StaticTextContext'
import {
  MAX_DISPLAY_NAME_LENGTH,
  MAX_LOCATION_LENGTH,
} from '../../../../../../services/community/index'
import { getMemberSinceYear } from '../../../../../../services/community/identity/library/content-utils'
import ShowOnProfileCheckboxGroup, { type ShowOnProfileCheckboxItem } from './ShowOnProfileCheckboxGroup'

const CLS = 'community-profile-settings'
const PLACEHOLDER_YEAR = '2000'

const MONTH_KEYS = [
  { value: '01', key: 'birthdayMonthJan' },
  { value: '02', key: 'birthdayMonthFeb' },
  { value: '03', key: 'birthdayMonthMar' },
  { value: '04', key: 'birthdayMonthApr' },
  { value: '05', key: 'birthdayMonthMay' },
  { value: '06', key: 'birthdayMonthJun' },
  { value: '07', key: 'birthdayMonthJul' },
  { value: '08', key: 'birthdayMonthAug' },
  { value: '09', key: 'birthdayMonthSep' },
  { value: '10', key: 'birthdayMonthOct' },
  { value: '11', key: 'birthdayMonthNov' },
  { value: '12', key: 'birthdayMonthDec' },
] as const

function getDaysInMonth (month: string): number {
  if (!month) return 31
  // Use a leap year so Feb can have 29
  return new Date(2000, parseInt(month, 10), 0).getDate()
}

function parseBirthdayMonth (birthday: string): string {
  if (!birthday) return ''
  const parts = birthday.split('-')
  return parts.length >= 2 ? parts[1] : ''
}

function parseBirthdayDay (birthday: string): string {
  if (!birthday) return ''
  const parts = birthday.split('-')
  return parts.length >= 3 ? parts[2] : ''
}

export interface InformationSectionProps {
  displayName: string
  onDisplayNameChange: (value: string) => void
  location: string
  onLocationChange: (value: string) => void
  locationVisibility: string
  onLocationVisibilityChange: (value: string) => void
  birthday: string
  onBirthdayChange: (value: string) => void
  birthdayVisibility: string
  onBirthdayVisibilityChange: (value: string) => void
  memberSince?: string
  memberSinceVisibility: string
  onMemberSinceVisibilityChange: (value: string) => void
  profileVisibility?: string
}

function InformationSection ({
  displayName,
  onDisplayNameChange,
  location,
  onLocationChange,
  locationVisibility,
  onLocationVisibilityChange,
  birthday,
  onBirthdayChange,
  birthdayVisibility,
  onBirthdayVisibilityChange,
  memberSince,
  memberSinceVisibility,
  onMemberSinceVisibilityChange,
  profileVisibility = 'public',
}: InformationSectionProps) {
  const birthdayMonth = parseBirthdayMonth(birthday)
  const birthdayDay = parseBirthdayDay(birthday)

  const isStealthMode = profileVisibility === 'private'
  const memberSinceYear = getMemberSinceYear(memberSince) || null

  const informationTitleText = useTranslatedText({ keys: ['informationTitle'] })
  const displayNameLabelText = useTranslatedText({ keys: ['displayNameLabel'] })
  const alwaysVisibleText = useTranslatedText({ keys: ['alwaysVisible'] })
  const displayNamePlaceholderText = useTranslatedText({ keys: ['displayNamePlaceholder'] })
  const locationLabelText = useTranslatedText({ keys: ['locationLabel'] })
  const locationPlaceholderText = useTranslatedText({ keys: ['locationPlaceholder'] })
  const birthdayLabelText = useTranslatedText({ keys: ['birthdayLabel'] })
  const birthdayMonthLabelText = useTranslatedText({ keys: ['birthdayMonthLabel'] })
  const birthdayDayLabelText = useTranslatedText({ keys: ['birthdayDayLabel'] })
  const showOnMyProfileText = useTranslatedText({ keys: ['showOnMyProfile'] })
  const memberSinceLabel = useTranslatedText({
    keys: ['memberSince'],
    params: { year: memberSinceYear ? String(memberSinceYear) : '' },
  })

  const dayOptions = useMemo(() => {
    const count = getDaysInMonth(birthdayMonth)
    return Array.from({ length: count }, (_, i) => {
      const d = String(i + 1).padStart(2, '0')
      return { value: d, label: String(i + 1) }
    })
  }, [birthdayMonth])

  const handleMonthChange = useCallback((newMonth: string) => {
    if (!newMonth) {
      onBirthdayChange('')
      return
    }
    const maxDay = getDaysInMonth(newMonth)
    const day = birthdayDay && parseInt(birthdayDay, 10) <= maxDay
      ? birthdayDay
      : '01'
    onBirthdayChange(`${PLACEHOLDER_YEAR}-${newMonth}-${day}`)
  }, [birthdayDay, onBirthdayChange])

  const handleDayChange = useCallback((newDay: string) => {
    if (!newDay) {
      onBirthdayChange('')
      return
    }
    const month = birthdayMonth || '01'
    onBirthdayChange(`${PLACEHOLDER_YEAR}-${month}-${newDay}`)
  }, [birthdayMonth, onBirthdayChange])

  const visibilityItems = useMemo<ShowOnProfileCheckboxItem[]>(() => {
    const items: ShowOnProfileCheckboxItem[] = [
      {
        id: 'location',
        label: locationLabelText,
        checked: !isStealthMode && locationVisibility !== 'private',
        onChange: (checked) => onLocationVisibilityChange(checked ? 'public' : 'private'),
        disabled: isStealthMode,
      },
      {
        id: 'birthday',
        label: birthdayLabelText,
        checked: !isStealthMode && birthdayVisibility !== 'private',
        onChange: (checked) => onBirthdayVisibilityChange(checked ? 'public' : 'private'),
        disabled: isStealthMode,
      },
    ]
    if (memberSinceYear) {
      items.push({
        id: 'memberSince',
        label: memberSinceLabel,
        checked: !isStealthMode && memberSinceVisibility !== 'private',
        onChange: (checked) => onMemberSinceVisibilityChange(checked ? 'public' : 'private'),
        disabled: isStealthMode,
      })
    }
    return items
  }, [
    locationLabelText, birthdayLabelText, isStealthMode, locationVisibility, birthdayVisibility,
    memberSinceVisibility, memberSinceYear, memberSinceLabel, onLocationVisibilityChange,
    onBirthdayVisibilityChange, onMemberSinceVisibilityChange,
  ])

  return (
    <div className={`${CLS}__card ${CLS}__card--bordered ${CLS}__card--info`}>
      <div className={`${CLS}__card-header`}>
        <div className={`${CLS}__card-header-left`}>
          <div>
            <H2 className={`${CLS}__card-title`}>
              {informationTitleText}
            </H2>
          </div>
        </div>
      </div>

      {/* Row 1: Display Name | Location */}
      <div className={`${CLS}__field-row`}>
        <div className={`${CLS}__field`}>
          <div className={`${CLS}__field-label-row`}>
            <label className={`${CLS}__field-label`} htmlFor="settings-display-name">
              {displayNameLabelText}
            </label>
            <span className={`${CLS}__field-controls`}>
              <span
                id="settings-display-name-badge"
                className={`${CLS}__badge ${CLS}__badge--visible`}
              >
                <EyeShowSolid />
                {alwaysVisibleText}
              </span>
            </span>
          </div>
          <input
            id="settings-display-name"
            type="text"
            className={`${CLS}__input`}
            value={displayName}
            onChange={(e) => onDisplayNameChange(e.target.value)}
            maxLength={MAX_DISPLAY_NAME_LENGTH}
            placeholder={displayNamePlaceholderText}
            aria-required="true"
            aria-invalid={displayName.trim().length === 0}
            aria-describedby="settings-display-name-badge"
          />
        </div>
        <div className={`${CLS}__field`}>
          <div className={`${CLS}__field-label-row`}>
            <label className={`${CLS}__field-label`} htmlFor="settings-location">
              {locationLabelText}
            </label>
          </div>
          <LocationAutocomplete
            id="settings-location"
            value={location}
            onChange={onLocationChange}
            onSelect={onLocationChange}
            maxLength={MAX_LOCATION_LENGTH}
            placeholder={locationPlaceholderText}
            ariaLabel={locationLabelText}
          />
        </div>
      </div>

      {/* Row 2: Birthday month | Birthday day */}
      <div className={`${CLS}__field-row`}>
        <div className={`${CLS}__field`}>
          <div className={`${CLS}__field-label-row`}>
            <label className={`${CLS}__field-label`} htmlFor="settings-birthday-month">
              {birthdayLabelText}
            </label>
          </div>
          <div className={`${CLS}__select-wrapper`}>
            <select
              id="settings-birthday-month"
              className={`${CLS}__input ${CLS}__input--select`}
              value={birthdayMonth}
              onChange={(e) => handleMonthChange(e.target.value)}
              aria-label={birthdayMonthLabelText}
            >
              <option value="">--</option>
              {MONTH_KEYS.map((m) => (
                <MonthOption key={m.value} value={m.value} translationKey={m.key} />
              ))}
            </select>
            <ChevronDown size="16px" className={`${CLS}__select-chevron`} />
          </div>
        </div>
        <div className={`${CLS}__field`}>
          <div className={`${CLS}__field-label-row`}>
            <span className={`${CLS}__field-label`} aria-hidden="true">&nbsp;</span>
          </div>
          <div className={`${CLS}__select-wrapper`}>
            <select
              id="settings-birthday-day"
              className={`${CLS}__input ${CLS}__input--select`}
              value={birthdayDay}
              onChange={(e) => handleDayChange(e.target.value)}
              aria-label={birthdayDayLabelText}
            >
              <option value="">--</option>
              {dayOptions.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
            <ChevronDown size="16px" className={`${CLS}__select-chevron`} />
          </div>
        </div>
      </div>

      {/* Show on my profile */}
      <ShowOnProfileCheckboxGroup
        label={showOnMyProfileText}
        items={visibilityItems}
      />
    </div>
  )
}

/**
 * Wrapper to resolve a single month's translated label without violating the
 * rules of hooks inside a map() loop.
 */
interface MonthOptionProps {
  value: string
  translationKey: string
}

function MonthOption ({ value, translationKey }: MonthOptionProps) {
  const label = useTranslatedText({ keys: [translationKey] })
  return <option value={value}>{label}</option>
}

export default InformationSection
